import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DataGrid,
  GridToolbar
} from '@mui/x-data-grid';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Badge,
  Stack,
  Alert,
  CircularProgress,
  ThemeProvider,
  createTheme,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { Download as DownloadIcon, ModeCommentOutlined as ModeCommentOutlinedIcon } from '@mui/icons-material';
import { iniciativasAPI } from '../services/api';
import './TablaOperaciones.css';

const TablaOperaciones = ({
  embedded = false,
  añosFiltro = [2026],
  searchTerm = '',
  estadosFiltro = [],
  areasFiltro = [],
  jefesFiltro = [],
  lineaNegocioFiltro = [],
  negocioFiltro = [],
  negociosFiltro = [],
  innovacionFiltro = [],
  tipoInnovacionFiltro = [],
  tipoFiltro = [],
  madurezFiltro = [],
  desvioFiltro = [],
  iniciativas: iniciativasProp = [],
  loading: loadingProp = false
}) => {
  const [iniciativas, setIniciativas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const gridRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  // Funciones para manejo de localStorage de columnas
  const getColumnVisibilityFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('operaciones_column_visibility');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading column visibility from localStorage:', error);
      return {};
    }
  };

  const saveColumnVisibilityToLocalStorage = (visibilityModel) => {
    try {
      localStorage.setItem('operaciones_column_visibility', JSON.stringify(visibilityModel));
    } catch (error) {
      console.error('Error saving column visibility to localStorage:', error);
    }
  };

  // Cargar visibilidad de columnas al inicializar
  useEffect(() => {
    const savedVisibility = getColumnVisibilityFromLocalStorage();
    setColumnVisibilityModel(savedVisibility);
  }, []);

  // Función para manejar cambios en visibilidad de columnas
  const handleColumnVisibilityModelChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    saveColumnVisibilityToLocalStorage(newModel);
  };

  // Función para restablecer visibilidad de columnas
  const resetColumnVisibility = () => {
    const defaultVisibility = {};
    setColumnVisibilityModel(defaultVisibility);
    saveColumnVisibilityToLocalStorage(defaultVisibility);
  };

  // Hook para manejar el scroll horizontal con arrastre
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (!scrollRef.current) return;
      setIsDragging(true);
      setStartX(e.pageX - scrollRef.current.offsetLeft);
      setScrollLeft(scrollRef.current.scrollLeft);
      scrollRef.current.style.cursor = 'grabbing';
      scrollRef.current.style.userSelect = 'none';
    };

    const handleMouseLeave = () => {
      if (!scrollRef.current) return;
      setIsDragging(false);
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = 'auto';
    };

    const handleMouseUp = () => {
      if (!scrollRef.current) return;
      setIsDragging(false);
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = 'auto';
    };

    const handleMouseMove = (e) => {
      if (!isDragging || !scrollRef.current) return;
      e.preventDefault();
      const x = e.pageX - scrollRef.current.offsetLeft;
      const walk = (x - startX) * 2; // Multiplicador para velocidad de scroll
      scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('mousedown', handleMouseDown);
      scrollElement.addEventListener('mouseleave', handleMouseLeave);
      scrollElement.addEventListener('mouseup', handleMouseUp);
      scrollElement.addEventListener('mousemove', handleMouseMove);

      // Prevenir el comportamiento por defecto de arrastre en enlaces e imágenes
      scrollElement.addEventListener('dragstart', (e) => e.preventDefault());

      return () => {
        scrollElement.removeEventListener('mousedown', handleMouseDown);
        scrollElement.removeEventListener('mouseleave', handleMouseLeave);
        scrollElement.removeEventListener('mouseup', handleMouseUp);
        scrollElement.removeEventListener('mousemove', handleMouseMove);
        scrollElement.removeEventListener('dragstart', (e) => e.preventDefault());
      };
    }
  }, [isDragging, startX, scrollLeft]);

  // Tema personalizado para integración con Bootstrap
  const theme = createTheme({
    palette: {
      primary: {
        main: '#0d6efd', // Bootstrap primary color
      },
      secondary: {
        main: '#6c757d', // Bootstrap secondary color
      },
    },
    components: {
      MuiDataGrid: {
        styleOverrides: {
          columnHeaders: {
            backgroundColor: 'white !important',
            color: 'black !important',
            fontWeight: 'bold !important',
            borderBottom: '2px solid #dee2e6 !important',
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'black !important',
              fontWeight: 'bold !important',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              color: 'black !important',
            },
            '& .MuiDataGrid-menuIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-sortIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-filterIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-columnSeparator': {
              color: '#dee2e6 !important',
            }
          },
          columnHeader: {
            backgroundColor: 'white !important',
            color: 'black !important',
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'black !important',
              fontWeight: 'bold !important',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              color: 'black !important',
            },
            '& .MuiDataGrid-menuIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-sortIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-filterIcon': {
              color: 'black !important',
            }
          },
          columnHeaderTitle: {
            color: 'black !important',
            fontWeight: 'bold !important',
          }
        }
      }
    }
  });

  useEffect(() => {
    if (embedded) {
      // Cuando está embebido, usar las iniciativas pasadas como props
      setIniciativas(iniciativasProp);
      setLoading(loadingProp);
    } else {
      // Cargar datos para todos los años seleccionados (modo standalone)
      cargarDatos(añosFiltro);
    }
  }, [embedded, iniciativasProp, loadingProp, añosFiltro]);

  const cargarDatos = async (años) => {
    try {
      console.log('Cargando iniciativas de operaciones para años:', años);
      setLoading(true);
      // Llamar al backend para cada año y combinar resultados
      let todasLasIniciativas = [];

      for (const año of años) {
        try {
          const response = await iniciativasAPI.getTablaOperaciones({ year: año });
          if (response.data && Array.isArray(response.data)) {
            todasLasIniciativas = [...todasLasIniciativas, ...response.data];
          }
        } catch (yearErr) {
          console.warn(`Error cargando datos para el año ${año}:`, yearErr);
        }
      }

      // Eliminar duplicados basándose en el ID
      const iniciativasUnicas = todasLasIniciativas.filter((iniciativa, index, arr) =>
        index === arr.findIndex(i => i.id === iniciativa.id)
      );

      setIniciativas(iniciativasUnicas);
      setError(null);

      // Debug temporal: verificar datos de euros_dedicacion
      if (iniciativasUnicas.length > 0) {
        console.log('Debug - Primera iniciativa datos de coste:', {
          id: iniciativasUnicas[0].id,
          nombre: iniciativasUnicas[0].nombre_de_la_oportunidad,
          euros_dedicacion: iniciativasUnicas[0].euros_dedicacion,
          horas_dedicadas: iniciativasUnicas[0].horas_dedicadas,
          importe_total_cliente: iniciativasUnicas[0].importe_total_cliente,
          coste_esperado: iniciativasUnicas[0].coste_esperado
        });
      }
    } catch (error) {
      console.error('Error al cargar iniciativas de operaciones:', error);
      setError('Error al cargar los datos. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear valores decimales
  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    }).format(value);
  };

  // Función para formatear fechas
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES');
  };

  // Función para formatear números con separación de miles
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('es-ES', { useGrouping: true }).format(value);
  };



  // Función para formatear porcentajes
  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return '-';
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      useGrouping: true
    }).format(percentage) + '%';
  };

  // Función para formatear horas
  const formatHours = (hours) => {
    if (!hours) return '-';
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    }).format(hours) + ' h';
  };

  const normalizeNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const raw = String(value).trim();
    if (!raw) return null;

    // Keep only number-relevant characters and normalize common ES formats.
    let cleaned = raw
      .replace(/\u00a0/g, '')
      .replace(/€/g, '')
      .replace(/%/g, '')
      .replace(/[hH]\b/g, '')
      .replace(/horas?/gi, '')
      .replace(/\s+/g, '');

    if (!/[0-9]/.test(cleaned)) return null;

    if (cleaned.includes(',') && cleaned.includes('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (cleaned.includes(',')) {
      cleaned = cleaned.replace(',', '.');
    }

    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const escapeCsvCell = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (/[;"\n\r]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const isColumnVisible = (field) => columnVisibilityModel[field] !== false;

  const getExportableColumns = () => {
    return columns.filter((column) => column.field && isColumnVisible(column.field));
  };

  const getExportCellValue = (row, field) => {
    switch (field) {
      case 'grado_desvio_valor':
        return getGradoDesvio(row).tooltip || '';
      case 'nombre_de_la_oportunidad':
        return row.nombre_de_la_oportunidad || '';
      case 'jerarquia': {
        const { padre_nombre, hijos, es_agregado } = row;
        if (es_agregado && Array.isArray(hijos) && hijos.length > 0) {
          return `Padre (${hijos.length} hijos)`;
        }
        if (padre_nombre) {
          return `Hijo de: ${padre_nombre}`;
        }
        return '';
      }
      case 'estado':
        return getEstadoDisplay(row.estado) || '';
      case 'fecha_de_inicio_real':
        return row.fecha_de_inicio_real || row.fecha_de_inicio_planificada || '';
      case 'fecha_de_fin_real':
        return row.fecha_de_fin_real || row.fecha_de_fin_planificada || '';
      case 'tipo_de_iniciativa': {
        const displayMap = {
          iniciativa: 'Iniciativa',
          servicio: 'Servicio',
          generica: 'Genérica',
          mantenimiento: 'Mantenimiento'
        };
        return displayMap[row.tipo_de_iniciativa] || row.tipo_de_iniciativa || '';
      }
      case 'madurez': {
        if (row.tipo_de_iniciativa !== 'iniciativa') return 'N/A';
        const displayMap = {
          poc: 'POC',
          piloto: 'Piloto',
          proyecto: 'Proyecto'
        };
        return displayMap[row.madurez] || row.madurez || '';
      }
      case 'innovacion':
        return row.innovacion ? 'Sí' : 'No';
      case 'tipo_innovacion': {
        if (row.tipo_innovacion === 'innocard') return 'INNOCARD';
        if (row.tipo_innovacion === 'innotrans') return 'INNOTRANS';
        return row.tipo_innovacion || 'Sin especificar';
      }
      case 'porcentaje_avance':
        return normalizeNumericValue(getGradoEjecucion(row));
      case 'porcentaje_avance_esperado': {
        const value = getPorcentajeAvanceEsperado(row);
        return value === '-' ? null : normalizeNumericValue(value);
      }
      case 'horas_esperado':
        return normalizeNumericValue(getHorasEsperado(row));
      case 'coste_esperado':
        return normalizeNumericValue(getCosteEsperado(row));
      case 'horas_dedicadas':
        return normalizeNumericValue(getHorasDedicadas(row));
      case 'coste_real_calculado':
        return normalizeNumericValue(getCosteReal(row));
      case 'horas_desarrollo_estimado':
        return normalizeNumericValue(getHorasDesarrollo(row));
      case 'coste_estimado_calculado':
        return normalizeNumericValue(getCosteEstimadoDesarrollo(row));
      case 'desvio_avance_calculado': {
        const value = getDesvioAvance(row);
        return value === '-' ? null : normalizeNumericValue(value);
      }
      case 'desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real':
        return normalizeNumericValue(row.desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real);
      case 'porcentaje_sobre_oferta': {
        const value = getPorcentajeSobreOferta(row);
        return value === '-' ? null : normalizeNumericValue(value);
      }
      case 'desviacion_coste_sobre_porcentaje_real':
        return normalizeNumericValue(row.desviacion_coste_sobre_porcentaje_real);
      case 'desvio_tiempo_calculado': {
        const value = getDesvioTiempo(row);
        return value === '-' ? null : normalizeNumericValue(value);
      }
      case 'desvio_coste_calculado': {
        const value = getDesvioCoste(row);
        return value === '-' ? null : normalizeNumericValue(value);
      }
      case 'updated_at':
        return row.updated_at || '';
      default:
        return row[field] ?? '';
    }
  };

  const PERCENT_EXPORT_FIELDS = new Set([
    'porcentaje_avance',
    'porcentaje_avance_esperado',
    'desvio_avance_calculado',
    'desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real',
    'porcentaje_sobre_oferta',
    'desviacion_coste_sobre_porcentaje_real',
    'desvio_tiempo_calculado',
    'desvio_coste_calculado'
  ]);

  const formatNumericForCsv = (value) => {
    return new Intl.NumberFormat('es-ES', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 10
    }).format(value);
  };

  const formatExportValueForCsv = (value, field) => {
    if (value === null || value === undefined || value === '') return '';

    if (typeof value === 'number') {
      const numericValue = PERCENT_EXPORT_FIELDS.has(field) ? value / 100 : value;
      return formatNumericForCsv(numericValue);
    }

    return value;
  };

  // Función para obtener el color del chip de estado
  const getEstadoColor = (estado) => {
    if (['finalizada', 'aprobado'].includes(estado)) return 'success';
    if (['en_curso', 'planificada'].includes(estado)) return 'primary';
    if (['rechazada', 'cancelada'].includes(estado)) return 'error';
    if (['propuesta', 'validacion_lider_tecnico'].includes(estado)) return 'warning';
    return 'default';
  };

  // Función para obtener el texto del estado
  const getEstadoDisplay = (estado) => {
    const estadoMap = {
      'oportunidad_identificada': 'Oportunidad identificada',
      'en_preparacion_propuesta': 'En preparación propuesta',
      'supervision': 'Supervisión',
      'entregada': 'Entregada',
      'rechazada': 'Rechazada',
      'aprobado': 'Aprobado',
      'planificada': 'Planificada',
      'en_curso': 'En curso',
      'bloqueada': 'Bloqueada',
      'cancelada': 'Cancelada',
      'finalizada': 'Finalizada',
    };
    return estadoMap[estado] || estado;
  };



  // Función para obtener el año de la iniciativa (basado en fecha de creación)
  const getAñoIniciativa = (iniciativa) => {
    // Priorizar el campo año si está disponible
    if (iniciativa.año) {
      return iniciativa.año;
    }
    // Fallback a las fechas existentes
    if (iniciativa.fecha_de_inicio_real) {
      return new Date(iniciativa.fecha_de_inicio_real).getFullYear();
    } else if (iniciativa.fecha_de_inicio_planificada) {
      return new Date(iniciativa.fecha_de_inicio_planificada).getFullYear();
    } else if (iniciativa.fecha_de_aprobacion_propuesta_firmada) {
      return new Date(iniciativa.fecha_de_aprobacion_propuesta_firmada).getFullYear();
    }
    return '-';
  };

  // Función para obtener el porcentaje de avance
  const getGradoEjecucion = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return 0;
    const val = iniciativa.porcentaje_avance;
    return val !== undefined && val !== null ? val : 0;
  };



  // Función para calcular el desvío de tiempo (porcentaje)
  const getDesvioTiempo = (iniciativa) => {
    // Validar que la iniciativa exista
    if (!iniciativa || typeof iniciativa !== 'object') return '-';

    // Usar el campo desviacion_tiempo que ya contiene el porcentaje
    if (iniciativa.desviacion_tiempo !== undefined && iniciativa.desviacion_tiempo !== null) {
      return iniciativa.desviacion_tiempo;
    } else {
      return '-';
    }
  };

  // Función para calcular el desvío de coste (% sobre estimado)
  const getDesvioCoste = (iniciativa) => {
    // Validar que la iniciativa exista
    if (!iniciativa || typeof iniciativa !== 'object') return '-';

    // Intentar usar desviacion_coste si está disponible
    if (iniciativa.desviacion_coste !== null && iniciativa.desviacion_coste !== undefined) {
      return iniciativa.desviacion_coste;
    } else {
      return '-';
    }
  };

  // Función para obtener el porcentaje de avance esperado
  const getPorcentajeAvanceEsperado = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return '-';
    const val = iniciativa.porcentaje_avance_esperado;
    return val !== undefined && val !== null ? val : '-';
  };

  // Función para obtener las horas dedicadas
  const getHorasDedicadas = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return 0;
    return iniciativa.horas_dedicadas || 0;
  };

  // Función para obtener las horas de desarrollo estimadas
  const getHorasDesarrollo = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return 0;
    return iniciativa.horas_desarrollo_estimado || 0;
  };

  // Función para obtener las horas esperadas
  const getHorasEsperado = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return 0;
    return iniciativa.horas_esperado || 0;
  };

  // Función para calcular el desvío de avance
  const getDesvioAvance = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return '-';

    // Usar el campo desviacion_tiempo que ya contiene el porcentaje
    if (iniciativa.desviacion_avance !== undefined && iniciativa.desviacion_avance !== null) {
      return iniciativa.desviacion_avance;
    } else {
      return '-';
    }
  };

  // Función para obtener el coste real (euros por dedicación)
  const getCosteReal = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return 0;
    return iniciativa.euros_dedicacion || 0;
  };

  // Función para obtener el coste estimado de desarrollo
  const getCosteEstimadoDesarrollo = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return 0;
    return iniciativa.coste_estimado_desarrollo || 0;
  };

  // Función para obtener el coste esperado
  const getCosteEsperado = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return 0;
    return iniciativa.coste_esperado || 0;
  };

  // Función para calcular el porcentaje sobre total oferta
  const getPorcentajeSobreOferta = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') return '-';
    return iniciativa.porcentaje_sobre_estimado || '-';
  };

  const getGradoDesvio = (iniciativa) => {
    if (!iniciativa || typeof iniciativa !== 'object') {
      return { nivel: 'sin_desvios', valor: 1, color: '#2e7d32', tooltip: 'Sin desvíos' };
    }

    // --- Fecha fin vencida con proyecto En curso (máxima prioridad) ---
    const fechaFinRaw = iniciativa.fecha_de_fin_real || iniciativa.fecha_de_fin_planificada;
    let fechaNivel = 0;
    let fechaMsg = null;
    if (fechaFinRaw && iniciativa.estado === 'en_curso') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const fechaFin = new Date(fechaFinRaw);
      fechaFin.setHours(0, 0, 0, 0);
      if (fechaFin < today) {
        fechaNivel = 2;
        fechaMsg = `Fecha fin superada (${fechaFin.toLocaleDateString('es-ES')})`;
      } else if (fechaFin.getTime() === today.getTime()) {
        fechaNivel = 1;
        fechaMsg = `Fecha fin es hoy (${fechaFin.toLocaleDateString('es-ES')})`;
      }
    }

    // --- % s/C. Oferta (verde < 100%, rojo >= 100%) ---
    const porcentajeSobreOferta = getPorcentajeSobreOferta(iniciativa);
    const ofertaText = porcentajeSobreOferta === '-' ? 'N/A' : formatPercentage(porcentajeSobreOferta);
    let ofertaNivel = 0;
    if (porcentajeSobreOferta !== '-' && porcentajeSobreOferta >= 100) ofertaNivel = 2;

    // --- Desvío planificación y Desvío coste ---
    const desvioPlan = getDesvioAvance(iniciativa);
    const desvioCosteRaw = iniciativa.desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real;
    const desvioCoste = (desvioCosteRaw !== null && desvioCosteRaw !== undefined) ? desvioCosteRaw : '-';

    const planVal = desvioPlan === '-' ? null : desvioPlan;
    const costeVal = desvioCoste === '-' ? null : desvioCoste;
    const planText = planVal === null ? 'N/A' : formatPercentage(planVal);
    const costeText = costeVal === null ? 'N/A' : formatPercentage(costeVal);

    let desvioNivel = 0;
    if ((planVal !== null && planVal >= 20) || (costeVal !== null && costeVal >= 20)) desvioNivel = 2;
    else if ((planVal !== null && planVal >= 10) || (costeVal !== null && costeVal >= 10)) desvioNivel = 1;

    // --- Nivel final: máxima severidad de los tres indicadores ---
    const nivelFinal = Math.max(fechaNivel, ofertaNivel, desvioNivel);

    const NIVELES = [
      { nivel: 'sin_desvios', valor: 1, color: '#2e7d32' },
      { nivel: 'medio',       valor: 2, color: '#ff9800' },
      { nivel: 'grave',       valor: 3, color: '#d32f2f' },
    ];

    const base = NIVELES[nivelFinal];

    let tooltip;
    if (nivelFinal === 0) {
      tooltip = `Sin desvíos significativos: % s/C. Oferta (${ofertaText}), Planificación (${planText}), Coste (${costeText})`;
    } else {
      const label = nivelFinal === 2 ? 'Desvío grave' : 'Desvío medio';
      const threshold = nivelFinal === 2 ? 20 : 10;
      // Fecha primero (máxima prioridad), luego % s/C. Oferta, luego desvíos
      const causes = [];
      if (fechaNivel >= nivelFinal && fechaMsg) causes.push(fechaMsg);
      if (ofertaNivel >= nivelFinal) causes.push(`% s/C. Oferta (${ofertaText})`);
      if (planVal !== null && planVal >= threshold) causes.push(`Desv. Planificación (${planText})`);
      if (costeVal !== null && costeVal >= threshold) causes.push(`Desv. Coste (${costeText})`);
      tooltip = `${label}: ${causes.join(', ')}`;
    }

    return { ...base, tooltip };
  };

  // Literal de comentario automático de Azure DevOps
  const COMENTARIO_AUTO_AZDEVOPS = '📝 Este comentario fue generado automáticamente durante la importación desde Azure DevOps.';

  // Función para construir el texto del tooltip del último comentario
  const getUltimoComentarioTooltip = (iniciativa) => {
    if (!iniciativa.ultimo_comentario || !iniciativa.tiene_comentarios_humanos) {
      return 'No se ha registrado ningún comentario';
    }
    const { autor_nombre, fecha_creacion, contenido } = iniciativa.ultimo_comentario;
    const fecha = new Date(fecha_creacion).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
    const esAutoGenerado = contenido && contenido.includes(COMENTARIO_AUTO_AZDEVOPS);
    const textoMostrar = esAutoGenerado ? COMENTARIO_AUTO_AZDEVOPS : contenido;
    return `${autor_nombre || 'Desconocido'} · ${fecha}\n${textoMostrar}`;
  };

  // Devuelve true si hay un comentario humano en los últimos 7 días
  const tieneComentarioReciente = (iniciativa) => {
    if (!iniciativa.ultimo_comentario || !iniciativa.tiene_comentarios_humanos) return false;
    const diffMs = Date.now() - new Date(iniciativa.ultimo_comentario.fecha_creacion).getTime();
    return diffMs < 7 * 24 * 60 * 60 * 1000;
  };

  // Si estamos embebidos y recibimos iniciativas por props, 
  // las iniciativas ya vienen filtradas desde Operaciones.js por todos los filtros
  // (años, estados, areas, jefes, negocios, innovación, tipo, madurez, etc.)
  // Solo aplicamos filtro de texto adicional si es necesario
  const iniciativasFiltradas = embedded
    ? (iniciativas || []).filter(iniciativa => {
      if (searchTerm === '') return true;
      return (
        iniciativa.nombre_de_la_oportunidad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.negocio_estructura?.negocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.negocio_estructura?.linea_de_negocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.negocio_estructura?.subnegocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.tecnologia_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.area_ejecucion_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.jefe_proyecto_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getEstadoDisplay(iniciativa.estado)?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    })
    : iniciativas.filter(iniciativa => {
      const year = getAñoIniciativa(iniciativa);
      if (!year || year === '-' || !añosFiltro.includes(Number(year))) return false;
      // Filtros combos - usar arrays vacíos para "todos"
      if (estadosFiltro.length > 0 && !estadosFiltro.includes(iniciativa.estado)) return false;
      if (areasFiltro.length > 0 && !areasFiltro.includes(iniciativa.tecnologia_nombre)) return false;
      if (jefesFiltro.length > 0 && !jefesFiltro.includes(iniciativa.jefe_proyecto_nombre)) return false;
      // Filtro de tipo de iniciativa
      if (tipoFiltro.length > 0 && !tipoFiltro.includes(iniciativa.tipo_de_iniciativa)) return false;
      // Filtro de madurez - solo aplica cuando tipo_de_iniciativa === 'iniciativa'
      if (madurezFiltro.length > 0 && iniciativa.tipo_de_iniciativa === 'iniciativa') {
        if (!madurezFiltro.includes(iniciativa.madurez)) return false;
      }
      // Filtros de jerarquía de negocio - aplicar en cascada respetando la jerarquía
      const lineaNegocio = iniciativa.negocio_estructura?.linea_de_negocio || iniciativa.linea_de_negocio || iniciativa.negocio_estructura_linea_de_negocio;
      const negocio = iniciativa.negocio_estructura?.negocio || iniciativa.negocio || iniciativa.negocio_estructura_negocio;
      const subnegocio = iniciativa.negocio_estructura_nombre;

      // Aplicar lógica de cascada normal
      if (negociosFiltro.length > 0) {
        if (!negociosFiltro.includes(subnegocio)) return false;
      } else if (negocioFiltro.length > 0) {
        if (!negocioFiltro.includes(negocio)) return false;
      } else if (lineaNegocioFiltro.length > 0) {
        if (!lineaNegocioFiltro.includes(lineaNegocio)) return false;
      }
      // Filtro texto
      if (searchTerm === '') return true;
      return (
        iniciativa.nombre_de_la_oportunidad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.negocio_estructura?.negocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.negocio_estructura?.linea_de_negocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.negocio_estructura?.subnegocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.tecnologia_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.area_ejecucion_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.jefe_proyecto_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getEstadoDisplay(iniciativa.estado)?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }).map(iniciativa => ({
      ...iniciativa,
      grado_desvio_valor: getGradoDesvio(iniciativa).valor, // Agregar el valor para ordenamiento
      fecha_inicio_calculada: iniciativa.fecha_de_inicio_real || iniciativa.fecha_de_inicio_planificada, // Fecha para ordenamiento
      fecha_fin_calculada: iniciativa.fecha_de_fin_real || iniciativa.fecha_de_fin_planificada, // Fecha para ordenamiento
      grado_ejecucion_calculado: getGradoEjecucion(iniciativa), // % Avance
      desvio_avance_calculado: getDesvioAvance(iniciativa), // Desvío planificación
      coste_real_calculado: getCosteReal(iniciativa), // Coste Real
      coste_estimado_calculado: getCosteEstimadoDesarrollo(iniciativa), // Coste Oferta
      desvio_tiempo_calculado: getDesvioTiempo(iniciativa), // Desvío tiempo
      desvio_coste_calculado: getDesvioCoste(iniciativa) // Desvío coste esperado 
    }));

  // Nota: Si estamos embedded, los filtros ya se aplicaron en Operaciones.js
  // y las iniciativas ya vienen filtradas. Solo aplicamos el filtro de búsqueda de texto local.

  // Función para calcular pageSize dinámico basado en cantidad de proyectos
  const getDefaultPageSize = (totalItems) => {
    if (totalItems <= 10) return 10;
    if (totalItems <= 25) return 25;
    if (totalItems <= 50) return 50;
    return 100;
  };

  const defaultPageSize = getDefaultPageSize(iniciativasFiltradas.length);

  // Log para depuración: ver cuándo se recalculan columnas y qué datos hay
  console.log('[TablaOperaciones] Render columns, iniciativasFiltradas:', iniciativasFiltradas);
  console.log('[TablaOperaciones] tipoFiltro:', tipoFiltro);
  console.log('[TablaOperaciones] madurezFiltro:', madurezFiltro);
  if (iniciativasFiltradas.length > 0) {
    console.log('[TablaOperaciones] Sample iniciativa tipo_de_iniciativa:', iniciativasFiltradas[0]?.tipo_de_iniciativa);
    console.log('[TablaOperaciones] Sample iniciativa madurez:', iniciativasFiltradas[0]?.madurez);
  }
  // Configuración de columnas para el DataGrid
  const columns = [

    {
      field: 'grado_desvio_valor',
      headerName: 'Desvío',
      flex: 0.5,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      type: 'number',
      valueGetter: (value, row) => {
        return getGradoDesvio(row).valor;
      },
      renderCell: (params) => {
        const grado = getGradoDesvio(params.row);
        return (
          <Tooltip title={grado.tooltip} arrow>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: grado.color,
                  border: '2px solid',
                  borderColor: grado.color,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              />
            </Box>
          </Tooltip>
        );
      }
    },
    {
      field: 'nombre_de_la_oportunidad',
      headerName: 'Nombre',
      flex: 1.5,
      minWidth: 250,
      align: 'left',
      headerAlign: 'center',
      renderCell: (params) => (
        <Tooltip
          title={
            <span style={{ whiteSpace: 'pre-line', fontSize: '0.8rem' }}>
              {getUltimoComentarioTooltip(params.row)}
            </span>
          }
          arrow
          placement="right"
        >
          <Box
            sx={{
              fontWeight: 'bold',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              justifyContent: 'flex-start',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(params);
            }}
          >
            <Link
              to={buildProjectUrl(params.row.id)}
              style={{
                textDecoration: 'none',
                color: '#0d6efd',
                fontWeight: 'bold'
              }}
            >
              {params.value || '-'}
            </Link>
            {tieneComentarioReciente(params.row) && (
              <ModeCommentOutlinedIcon
                sx={{
                  fontSize: '14px',
                  color: '#7b9fd4',
                  flexShrink: 0,
                  opacity: 0.8,
                }}
              />
            )}
          </Box>
        </Tooltip>
      )
    },
    {
      field: 'jerarquia',
      headerName: 'Jerarquía',
      flex: 0.8,
      minWidth: 150,
      align: 'left',
      headerAlign: 'center',
      renderCell: (params) => {
        const { padre, padre_nombre, hijos, es_agregado } = params.row;

        if (es_agregado && hijos && hijos.length > 0) {
          return (
            <Tooltip title={hijos.map(h => h.nombre).join(', ')}>
              <Chip
                label={`Padre (${hijos.length} hijos)`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: '0.7rem' }}
              />
            </Tooltip>
          );
        }

        if (padre_nombre) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span style={{ fontSize: '0.7rem', color: '#666' }}>Hijo de:</span>
              <Link
                to={buildProjectUrl(padre)}
                style={{
                  textDecoration: 'none',
                  color: '#0d6efd',
                  fontSize: '0.8rem',
                  maxWidth: '100px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {padre_nombre}
              </Link>
            </Box>
          );
        }

        return <span style={{ color: '#ccc', fontSize: '0.7rem' }}>-</span>;
      }
    },
    {
      field: 'estado',
      headerName: 'Estado',
      flex: 0.8,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={getEstadoDisplay(params.value)}
          color={getEstadoColor(params.value)}
          variant="filled"
          size="small"
        />
      )
    },
    {
      field: 'jefe_proyecto_nombre',
      headerName: 'JP',
      flex: 0.9,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'fecha_de_inicio_real',
      headerName: 'Inicio',
      flex: 0.7,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      type: 'date',
      valueGetter: (value, row) => {
        const fecha = value || row.fecha_de_inicio_planificada;
        return fecha ? new Date(fecha) : null;
      },
      renderCell: (params) => formatDate(params.value || params.row.fecha_de_inicio_planificada)
    },
    {
      field: 'fecha_de_fin_real',
      headerName: 'Fin',
      flex: 0.7,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      type: 'date',
      valueGetter: (value, row) => {
        const fecha = value || row.fecha_de_fin_planificada;
        return fecha ? new Date(fecha) : null;
      },
      renderCell: (params) => formatDate(params.value || params.row.fecha_de_fin_planificada)
    },
    {
      field: 'porcentaje_avance',
      headerName: '% Avance',
      flex: 0.6,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      renderCell: (params) => {
        // Usar el valor directamente de la fila
        const porcentaje = params && params.row && params.row.porcentaje_avance !== undefined && params.row.porcentaje_avance !== null && !isNaN(params.row.porcentaje_avance)
          ? Number(params.row.porcentaje_avance)
          : 0;
        let color = '#2196f3';
        if (porcentaje >= 100) color = '#4caf50';
        return (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: 0.5
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
              {porcentaje}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(Number(porcentaje), 100)}
              sx={{
                width: '80%',
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: color,
                  borderRadius: 3
                }
              }}
            />
          </Box>
        );
      }
    },
    {
      field: 'tecnologia_nombre',
      headerName: 'Área conocimiento',
      flex: 0.9,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'area_ejecucion_nombre',
      headerName: 'Área ejecución',
      flex: 0.9,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'negocio_estructura_nombre',
      headerName: 'Negocio',
      flex: 1,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'tipo_de_iniciativa',
      headerName: 'Tipo',
      flex: 0.8,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const tipo = params.value;
        const displayMap = {
          'iniciativa': 'Iniciativa',
          'servicio': 'Servicio',
          'generica': 'Genérica',
          'mantenimiento': 'Mantenimiento'
        };
        return (
          <Chip
            label={displayMap[tipo] || tipo || '-'}
            color="info"
            variant="outlined"
            size="small"
            sx={{
              fontSize: '0.75rem',
              height: '20px',
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
        );
      }
    },
    {
      field: 'madurez',
      headerName: 'Madurez',
      flex: 0.7,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.row.tipo_de_iniciativa !== 'iniciativa') {
          return <Typography variant="body2" color="text.secondary">N/A</Typography>;
        }
        const madurez = params.value;
        const displayMap = {
          'poc': 'POC',
          'piloto': 'Piloto',
          'proyecto': 'Proyecto'
        };
        return (
          <Chip
            label={displayMap[madurez] || madurez || '-'}
            color="secondary"
            variant="outlined"
            size="small"
            sx={{
              fontSize: '0.75rem',
              height: '20px',
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
        );
      }
    },
    {
      field: 'innovacion',
      headerName: 'Innovación',
      flex: 0.6,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Sí' : 'No'}
          color={params.value ? 'primary' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
          size="small"
          sx={{
            fontSize: '0.75rem',
            height: '20px',
            '& .MuiChip-label': {
              px: 1
            }
          }}
        />
      )
    },
    {
      field: 'tipo_innovacion',
      headerName: 'Tipo Innovación',
      flex: 0.8,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {

        const tipo = params.value;
        let label = '-';
        let color = 'default';

        if (tipo === 'innocard') {
          label = 'INNOCARD';
          color = 'secondary';
        } else if (tipo === 'innotrans') {
          label = 'INNOTRANS';
          color = 'info';
        } else if (!tipo) {
          label = 'Sin especificar';
          color = 'warning';
        }

        return (
          <Chip
            label={label}
            color={color}
            variant="outlined"
            size="small"
            sx={{
              fontSize: '0.75rem',
              height: '20px',
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
        );
      }
    },
    {
      field: 'porcentaje_avance_esperado',
      headerName: '% Avance Esperado',
      flex: 0.7,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const porcentaje = getPorcentajeAvanceEsperado(params.row);
        if (porcentaje === '-') return <Typography variant="body2" color="text.secondary">-</Typography>;
        return (
          <Typography variant="body2" color="info.main" sx={{ fontSize: '0.85rem' }}>
            {formatPercentage(porcentaje)}
          </Typography>
        );
      }
    },
    {
      field: 'horas_esperado',
      headerName: 'Horas Esperadas',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const horas = getHorasEsperado(params.row);
        return (
          <Typography variant="body2" color="info.main" sx={{ fontSize: '0.85rem' }}>
            {formatHours(horas)}
          </Typography>
        );
      }
    },
    {
      field: 'coste_esperado',
      headerName: 'Coste Esperado',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
       renderHeader: () => (
        <Tooltip title="(%Avance esperado / Coste oferta). Valor de referencia del coste real que debería llevar el proyecto en el momento actual, según el avance esperado. El avance esperado tiene en cuenta únicamente el día actual respecto a las fechas de las épicas. Si el coste esperado es mayor que el real, suele indicar que se le ha dedicado más recursos de lo planificado al proyecto, y si es negativo, indica que se le han dedicado menos recursos." arrow>
          <span style={{ fontWeight: 'bold', cursor: 'help' }}>Coste Esperado</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        const coste = getCosteEsperado(params.row);
        return (
          <Typography variant="body2" color="info.main" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
            {formatCurrency(coste)}
          </Typography>
        );
      }
    },
    // ...eliminada columna duplicada de % Avance...
    {
      field: 'horas_dedicadas',
      headerName: 'Dedicaciones (h)',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const horas = getHorasDedicadas(params.row);
        return (
          <Typography variant="body2" color="success.main" sx={{ fontSize: '0.85rem' }}>
            {formatHours(horas)}
          </Typography>
        );
      }
    },
    {
      field: 'coste_real_calculado',
      headerName: 'Coste Real',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      valueGetter: (value, row) => {
        return getCosteReal(row);
      },
      renderCell: (params) => {
        const coste = getCosteReal(params.row);
        return (
          <Typography variant="body2" color="success.main" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
            {formatCurrency(coste)}
          </Typography>
        );
      }
    },
    {
      field: 'horas_desarrollo_estimado',
      headerName: 'Horas Propuesta',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const horas = getHorasDesarrollo(params.row);
        return (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {formatHours(horas)}
          </Typography>
        );
      }
    },
    {
      field: 'coste_estimado_calculado',
      headerName: 'Coste Oferta',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      valueGetter: (value, row) => {
        return getCosteEstimadoDesarrollo(row);
      },
      renderCell: (params) => {
        const coste = getCosteEstimadoDesarrollo(params.row);
        return (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {formatCurrency(coste)}
          </Typography>
        );
      }
    },
    {
      field: 'desvio_avance_calculado',
      headerName: 'Desvío planificación',
      flex: 0.9,
      minWidth: 140,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      valueGetter: (value, row) => {
        const desvio = getDesvioAvance(row);
        return desvio === '-' ? null : desvio;
      },
      renderHeader: () => (
        <Tooltip title="(%Avance esperado - %Avance) Indica el desvío del proyecto respecto a la planificación, teniendo en cuenta el avance real declarado. Un valor positivo de 10% indica que el avance esperado es superior al real." arrow>
          <span style={{ fontWeight: 'bold', cursor: 'help' }}>Desvío planificación</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        const desvio = getDesvioAvance(params.row);
        if (desvio === '-') return <Typography variant="body2" color="text.secondary">-</Typography>;
        const color = desvio >= 20 ? '#d32f2f' : desvio >= 10 ? '#ff9800' : '#2e7d32';
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              color: color,
              fontWeight: desvio >= 20 ? 'bold' : 'normal'
            }}
          >
            {formatPercentage(desvio)}
          </Typography>
        );
      }
    },
    {
      field: 'desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real',
      headerName: 'Desvío coste',
      flex: 1,
      minWidth: 140,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title=" [(Coste real / Coste oferta) - Avance] Indica la diferencia entre el coste de la oferta consumido y el avance real. Este indicador muestra el desvío actual del proyecto real, indicando que si el proyecto avanza conforme a la planificación (sin más desvíos en el futuro) el desvío al finalizar el proyecto será el indicado. " arrow>
          <span style={{ fontWeight: 'bold', cursor: 'help' }}>Desvío coste</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        const desvio = params.row.desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real;
        if (desvio === null || desvio === undefined) return <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>-</Typography>;

        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              color: desvio < 10 ? '#2e7d32' : desvio >= 20 ? '#d32f2f' : '#ff9800',
              fontWeight: desvio >= 20 ? 'bold' : 'normal'
            }}
          >
            {formatPercentage(desvio)}
          </Typography>
        );
      }
    },
    {
      field: 'porcentaje_sobre_oferta',
      headerName: '% s/C. Oferta',
      flex: 0.7,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      valueGetter: (value, row) => {
        const porcentaje = getPorcentajeSobreOferta(row);
        return porcentaje === '-' ? null : porcentaje;
      },
      renderHeader: () => (
        <Tooltip title="(Coste real / Coste oferta) % de coste real respecto al coste de oferta." arrow>
          <span style={{ fontWeight: 'bold', cursor: 'help' }}>% s/C. Oferta</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        const porcentaje = getPorcentajeSobreOferta(params.row);
        if (porcentaje === '-') return <Typography variant="body2" color="text.secondary">-</Typography>;

        const color = porcentaje >= 100 ? '#d32f2f' : '#2e7d32';
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              color: color,
              fontWeight: porcentaje >= 100 ? 'bold' : 'normal'
            }}
          >
            {formatPercentage(porcentaje)}
          </Typography>
        );
      }
    },
    {
      field: 'desviacion_coste_sobre_porcentaje_real',
      headerName: 'Desvío coste predicción',
      flex: 1,
      minWidth: 140,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title=" [(Coste real / (Avance * Coste oferta) )-1] Indica la tendencia de desvío actual del proyecto, indicando que, bajo la tendencia actual (si el proyecto continúa desviandose del mismo modo en le futuro) el desvío al finalizar el proyecto será el indicado." arrow>
          <span style={{ fontWeight: 'bold', cursor: 'help' }}>Desvío coste predicción</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        const desvio = params.row.desviacion_coste_sobre_porcentaje_real;
        if (desvio === null || desvio === undefined) return <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>-</Typography>;

        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              color: desvio < 10 ? '#2e7d32' : desvio >= 20 ? '#d32f2f' : '#ff9800',
              fontWeight: desvio >= 20 ? 'bold' : 'normal'
            }}
          >
            {formatPercentage(desvio)}
          </Typography>
        );
      }
    },
    {
      field: 'desvio_tiempo_calculado',
      headerName: 'Desvío horas esperadas',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      valueGetter: (value, row) => {
        const desvio = getDesvioTiempo(row);
        return desvio === '-' ? null : desvio;
      },
      renderHeader: () => (
        <Tooltip title="Desvio coste esperado expresado en horas" arrow>
          <span style={{ fontWeight: 'bold', cursor: 'help' }}>Desvío horas esperadas</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        const desvio = getDesvioTiempo(params.row);
        if (desvio === '-') return <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>-</Typography>;

        const absDesvio = Math.abs(desvio);
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              color: absDesvio < 10 ? '#2e7d32' : absDesvio >= 20 ? '#d32f2f' : '#ff9800',
              fontWeight: absDesvio >= 20 ? 'bold' : 'normal'
            }}
          >
            {formatPercentage(desvio)}
          </Typography>
        );
      }
    },
    {
      field: 'desvio_coste_calculado',
      headerName: 'Desvío coste esperado',
      flex: 1,
      minWidth: 140,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      valueGetter: (value, row) => {
        const desvio = getDesvioCoste(row);
        return desvio === '-' ? null : desvio;
      },
      renderHeader: () => (
        <Tooltip title="Porcentaje de desviación entre el coste real y el coste esperado del proyecto segun el avance de la planificación" arrow>
          <span style={{ fontWeight: 'bold', cursor: 'help' }}>Desvío coste esperado</span>
        </Tooltip>
      ),
      renderCell: (params) => {
        const desvio = getDesvioCoste(params.row);
        if (desvio === '-') return <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>-</Typography>;

        const absDesvio = Math.abs(desvio);
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              color: absDesvio < 10 ? '#2e7d32' : absDesvio >= 20 ? '#d32f2f' : '#ff9800',
              fontWeight: absDesvio >= 20 ? 'bold' : 'normal'
            }}
          >
            {formatPercentage(desvio)}
          </Typography>
        );
      }
    },
    {
      field: 'updated_at',
      headerName: 'Última actualización',
      flex: 0.8,
      minWidth: 160,
      align: 'center',
      headerAlign: 'center',
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => {
        if (!params.value) return '-';
        return params.value.toLocaleString('es-ES', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        });
      }
    },
  ];

  // Función auxiliar para construir URL con estado actual
  const buildProjectUrl = (projectId) => {   
    const currentState = {
      from: 'operaciones',
      tab: '1', // Tab de tabla
      search: searchTerm || '',
      estados: estadosFiltro.join(','),
      areas: areasFiltro.join(','),
      jefes: jefesFiltro.join(','),
      lineaNegocio: lineaNegocioFiltro.join(','),
      negocio: negocioFiltro.join(','),
      negocios: negociosFiltro.join(','),
      innovacion: innovacionFiltro,
      tipoInnovacion: tipoInnovacionFiltro,
      años: añosFiltro.join(',')
    };

    // Filtrar parámetros vacíos
    const filteredParams = Object.fromEntries(
      Object.entries(currentState).filter(([key, value]) => value && value !== '')
    );

    const queryString = new URLSearchParams(filteredParams).toString();
    return `/projects/${projectId}?${queryString}`;
  };

  // Manejo del click en la fila
  const handleRowClick = (params) => {
    // Solo navegar si no estamos arrastrando
    if (!isDragging) {
      // Construir parámetros de estado actual para la navegación de vuelta
      const currentState = {
        from: 'operaciones',
        tab: '1', // Tab de tabla
        search: searchTerm || '',
        estados: estadosFiltro.join(','),
        areas: areasFiltro.join(','),
        jefes: jefesFiltro.join(','),
        lineaNegocio: lineaNegocioFiltro.join(','),
        negocio: negocioFiltro.join(','),
        negocios: negociosFiltro.join(','),
        innovacion: innovacionFiltro,
        tipoInnovacion: tipoInnovacionFiltro,
        años: añosFiltro.join(',')
      };

      // Filtrar parámetros vacíos
      const filteredParams = Object.fromEntries(
        Object.entries(currentState).filter(([key, value]) => value && value !== '')
      );

      const queryString = new URLSearchParams(filteredParams).toString();
      navigate(`/projects/${params.id}?${queryString}`);
    }
  };

  if (loading) {
    return (
      <Box className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>Cargando...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={() => cargarDatos(añosFiltro)}>
          Reintentar
        </Button>
      }>
        <Typography variant="h6">Error</Typography>
        {error}
      </Alert>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        px: embedded ? 0 : { xs: 1, sm: 2, md: 3 },
        py: embedded ? 0 : 2
      }}>
        {/* Header - Solo mostrar si no está embebido */}
        {!embedded && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            mb: 4
          }}>
            <Box>
              <Typography variant="h4" component="h2" sx={{
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}>
                <i className="fas fa-cogs text-primary me-2"></i>
                Tabla Operaciones
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                Proyectos operacionales del año {new Date().getFullYear()} (Aprobado, Planificada, En curso, Bloqueada, Cancelada, Finalizada)
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="contained"
                color="success"
                onClick={() => navigate('/crear-oportunidad')}
                fullWidth={{ xs: true, sm: false }}
              >
                <i className="fas fa-plus me-2"></i>
                Crear Oportunidad
              </Button>
              <Badge badgeContent={iniciativasFiltradas.length} color="primary">
                <Typography variant="body2">
                  iniciativa{iniciativasFiltradas.length !== 1 ? 's' : ''}
                </Typography>
              </Badge>
            </Stack>
          </Box>
        )}

        {/* DataGrid */}
        <Card sx={{
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 170px)', md: 'calc(100vh - 140px)' },
          boxShadow: 3,
          borderRadius: 2
        }}>
          <CardContent sx={{
            p: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* DataGrid with responsive scrolling */}
            <Box
              sx={{
                flex: 1,
                width: '100%',
                minHeight: 0, // Important for flex shrinking
                position: 'relative'
              }}
              ref={scrollRef}
              className={`mui-datagrid-container ${isDragging ? 'dragging' : ''}`}
            >
              {console.log('[TablaOperaciones] Render DataGrid, rows:', iniciativasFiltradas)}
              <DataGrid
                ref={gridRef}
                rows={iniciativasFiltradas}
                columns={columns}
                pinnedColumns={{ left: ['grado_desvio_valor', 'nombre_de_la_oportunidad'] }}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: defaultPageSize,
                    },
                  },
                  sorting: {
                    sortModel: [{ field: 'updated_at', sort: 'desc' }],
                  },
                  columns: {
                    columnVisibilityModel: columnVisibilityModel,
                  },
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                checkboxSelection={false}
                disableRowSelectionOnClick={true}
                autoHeight={false}
                disableColumnSelector={false}
                disableColumnMenu={false}
                disableColumnSorting={false}
                sortingMode="client"
                slots={{
                  toolbar: GridToolbar,
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                sx={{
                  border: 'none',
                  height: '100%',
                  width: '100%',
                  '& .MuiDataGrid-main': {
                    overflow: 'auto'
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    // Ensure proper scrolling behavior
                    overflowX: 'auto',
                    overflowY: 'auto'
                  },
                  '& .MuiDataGrid-cell': {
                    cursor: 'default', // Cursor normal para las celdas
                    textAlign: 'center !important',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  },
                  '& .MuiDataGrid-cell[data-field="nombre_de_la_oportunidad"]': {
                    textAlign: 'left !important',
                    justifyContent: 'flex-start'
                  },
                  // Estilos para columnas fijas
                  '& .MuiDataGrid-cell--pinnedLeft': {
                    backgroundColor: 'white',
                    zIndex: 10,
                  },
                  '& .MuiDataGrid-cell--pinnedLeft:not(:last-of-type)::after': {
                    content: '""',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    backgroundColor: 'rgba(224, 224, 224, 1)',
                  },
                  '& .MuiDataGrid-columnHeader--pinnedLeft': {
                    backgroundColor: 'white !important',
                    zIndex: 11,
                  },
                  '& .MuiDataGrid-columnHeader--pinnedLeft:not(:last-of-type)::after': {
                    content: '""',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    backgroundColor: 'rgba(224, 224, 224, 1)',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)', // Mantener hover visual
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'white !important',
                    color: 'black !important',
                    fontWeight: 'bold !important',
                    borderBottom: '2px solid #dee2e6 !important',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: 'black !important',
                      fontWeight: 'bold !important',
                    }
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: 'white !important',
                    color: 'black !important',
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: 'black !important',
                      fontWeight: 'bold !important',
                    }
                  },
                  '& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator': {
                    color: '#dee2e6 !important',
                  },
                  '& .MuiDataGrid-menuIcon': {
                    color: 'black !important',
                  },
                  '& .MuiDataGrid-sortIcon': {
                    color: 'black !important',
                  },
                  '& .MuiDataGrid-filterIcon': {
                    color: 'black !important',
                  },
                  '& .MuiDataGrid-iconButtonContainer': {
                    color: 'black !important',
                  },
                  '& .MuiDataGrid-columnHeaderTitleContainer': {
                    color: 'black !important',
                  },
                  // Estilos para la toolbar
                  '& .MuiDataGrid-toolbarContainer': {
                    padding: '8px',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #dee2e6',
                    '& .MuiButton-root': {
                      color: '#495057',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      }
                    },
                    '& .MuiInputBase-root': {
                      backgroundColor: 'white',
                      '& .MuiInputBase-input': {
                        padding: '6px 8px',
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  // Responsive behavior
                  '& .MuiDataGrid-cell--textLeft': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
                localeText={{
                  // Textos en español
                  noRowsLabel: 'No se encontraron proyectos operacionales',
                  noResultsOverlayLabel: 'No se encontraron resultados',
                  errorOverlayDefaultLabel: 'Ha ocurrido un error',
                  toolbarColumns: 'Columnas',
                  toolbarFilters: 'Filtros',
                  toolbarDensity: 'Densidad',
                  toolbarExport: 'Exportar',
                  columnsPanelTextFieldLabel: 'Encontrar columna',
                  columnsPanelTextFieldPlaceholder: 'Título de columna',
                  columnsPanelDragIconLabel: 'Reordenar columna',
                  columnsPanelShowAllButton: 'Mostrar todas',
                  columnsPanelHideAllButton: 'Ocultar todas',
                  filterPanelAddFilter: 'Añadir filtro',
                  filterPanelDeleteIconLabel: 'Borrar',
                  filterPanelOperators: 'Operadores',
                  filterPanelOperatorAnd: 'Y',
                  filterPanelOperatorOr: 'O',
                  filterPanelColumns: 'Columnas',
                  filterPanelInputLabel: 'Valor',
                  filterPanelInputPlaceholder: 'Valor del filtro',
                  filterOperatorContains: 'contiene',
                  filterOperatorEquals: 'es igual a',
                  filterOperatorStartsWith: 'comienza por',
                  filterOperatorEndsWith: 'termina en',
                  filterOperatorIsEmpty: 'está vacío',
                  filterOperatorIsNotEmpty: 'no está vacío',
                  columnMenuLabel: 'Menú',
                  columnMenuShowColumns: 'Mostrar columnas',
                  columnMenuFilter: 'Filtro',
                  columnMenuHideColumn: 'Ocultar',
                  columnMenuUnsort: 'Desordenar',
                  columnMenuSortAsc: 'Ordenar ASC',
                  columnMenuSortDesc: 'Ordenar DESC',
                  columnHeaderFiltersTooltipActive: (count) =>
                    count !== 1 ? `${count} filtros activos` : `${count} filtro activo`,
                  columnHeaderFiltersLabel: 'Mostrar filtros',
                  columnHeaderSortIconLabel: 'Ordenar',
                  footerRowSelected: (count) =>
                    count !== 1
                      ? `${count.toLocaleString('es-ES')} filas seleccionadas`
                      : `${count.toLocaleString('es-ES')} fila seleccionada`,
                  footerTotalRows: 'Filas totales:',
                  footerTotalVisibleRows: (visibleCount, totalCount) =>
                    `${visibleCount.toLocaleString('es-ES')} de ${totalCount.toLocaleString('es-ES')}`,
                  checkboxSelectionHeaderName: 'Selección',
                  booleanCellTrueLabel: 'sí',
                  booleanCellFalseLabel: 'no',
                  actionsCellMore: 'más',
                  pinToLeft: 'Anclar a la izquierda',
                  pinToRight: 'Anclar a la derecha',
                  unpin: 'Desanclar',
                  treeDataGroupingHeaderName: 'Grupo',
                  treeDataExpand: 'ver hijos',
                  treeDataCollapse: 'ocultar hijos',
                  toolbarDensityLabel: 'Densidad',
                  toolbarDensityCompact: 'Compacta',
                  toolbarDensityStandard: 'Estándar',
                  toolbarDensityComfortable: 'Cómoda',
                  toolbarExportLabel: 'Exportar',
                  toolbarExportCSV: 'Descargar como CSV',
                  toolbarExportPrint: 'Imprimir',
                  // Textos para columnas
                  toolbarColumnsLabel: 'Seleccionar columnas',
                  columnsManagementSearchTitle: 'Buscar columna',
                  columnsManagementNoColumns: 'No hay columnas',
                  columnsManagementShowHideAllText: 'Mostrar/Ocultar todas',
                  columnsManagementReset: 'Restablecer'
                }}
              />

              {/* Botones superpuestos sobre la paginación */}
              <Button
                onClick={resetColumnVisibility}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 140,
                  minWidth: 'auto',
                  width: 'auto',
                  height: 32,
                  padding: '0 8px',
                  color: '#6c757d',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '16px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#495057'
                  }
                }}
              >
                Restablecer columnas
              </Button>

              {/* Botón de exportar superpuesto sobre la paginación */}
              <Button
                onClick={() => {
                  const exportColumns = getExportableColumns();

                  const headerRow = exportColumns.map((column) => {
                    if (typeof column.headerName === 'string' && column.headerName.trim()) {
                      return column.headerName;
                    }
                    return column.field;
                  });

                  const dataRows = iniciativasFiltradas.map((row) =>
                    exportColumns.map((column) => {
                      const rawValue = getExportCellValue(row, column.field);
                      return formatExportValueForCsv(rawValue, column.field);
                    })
                  );

                  const csvContent = [headerRow, ...dataRows]
                    .map((row) => row.map(escapeCsvCell).join(';'))
                    .join('\n');

                  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = `operaciones_${new Date().toISOString().split('T')[0]}.csv`;
                  link.click();
                }}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 16,
                  minWidth: 'auto',
                  width: 'auto',
                  height: 32,
                  padding: '0 8px',
                  color: '#6c757d',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '16px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#495057'
                  }
                }}
              >
                Exportar tabla
                <DownloadIcon sx={{ fontSize: 16, ml: 0.5 }} />
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default TablaOperaciones;